/**
 * @voltxampere/persistence — IndexedDB open + upgrade (Sprint 21)
 *
 * Single shared database instance per page. `openDb()` is
 * idempotent: subsequent calls reuse the cached `IDBDatabase` so
 * the auto-save loop doesn't reopen the connection on every save.
 *
 * Schema (DB v1):
 *
 *   circuits   { keyPath: "id" }
 *     index    by_updatedAt → "updatedAt"
 *
 *   recents    { keyPath: "slot" }
 *
 * Sprint 22+ migrations switch on `event.oldVersion` to apply
 * incremental changes (e.g. adding an `assets` store).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { DB_NAME, DB_VERSION, INDEX_BOOKMARKS_CIRCUIT_ID, INDEX_CIRCUITS_UPDATED_AT, STORE_BOOKMARKS, STORE_CIRCUITS, STORE_COMMAND_RECENTS, STORE_RECENTS, STORE_SETTINGS, } from "./types.js";
let cachedDb = null;
let openPromise = null;
/**
 * Open (or return the cached) IndexedDB instance. Throws on:
 *  - hostile environments where `indexedDB` is undefined
 *  - blocked upgrades (another tab holds a stale connection)
 *  - unrecoverable IDB errors during the open transaction
 */
export function openDb() {
    if (cachedDb)
        return Promise.resolve(cachedDb);
    if (openPromise)
        return openPromise;
    if (typeof indexedDB === "undefined") {
        return Promise.reject(new Error("openDb: indexedDB is not available in this environment"));
    }
    openPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (event) => {
            const db = req.result;
            const oldVersion = event.oldVersion || 0;
            // DB v1 — circuits + recents object stores.
            if (oldVersion < 1) {
                if (!db.objectStoreNames.contains(STORE_CIRCUITS)) {
                    const circuits = db.createObjectStore(STORE_CIRCUITS, { keyPath: "id" });
                    circuits.createIndex(INDEX_CIRCUITS_UPDATED_AT, "updatedAt", { unique: false });
                }
                if (!db.objectStoreNames.contains(STORE_RECENTS)) {
                    db.createObjectStore(STORE_RECENTS, { keyPath: "slot" });
                }
            }
            // DB v2 (Sprint 22 C4) — command palette recent ring. Single
            // row keyed by COMMAND_RECENTS_SENTINEL_KEY; we read/write the
            // full ordered id list atomically.
            if (oldVersion < 2) {
                if (!db.objectStoreNames.contains(STORE_COMMAND_RECENTS)) {
                    db.createObjectStore(STORE_COMMAND_RECENTS, { keyPath: "key" });
                }
            }
            // DB v3 (Sprint 23 C1) — single-row "settings" store. Holds
            // theme + locale + AC/transient defaults + custom shortcuts +
            // onboarding tour state + a11y prefs in one atomic row keyed
            // by SETTINGS_SENTINEL_KEY. Sprint 24+ field additions stay
            // backward-compatible (all fields optional).
            if (oldVersion < 3) {
                if (!db.objectStoreNames.contains(STORE_SETTINGS)) {
                    db.createObjectStore(STORE_SETTINGS, { keyPath: "key" });
                }
            }
            // DB v4 (Sprint 32-4) — per-row bookmarks store. Each row is a
            // StoredBookmark keyed by `id`; the `by_circuitId` index lets
            // the per-preset load path fetch a circuit's pin set without
            // scanning the whole store. The migration is purely additive —
            // existing circuits / recents / commandRecents / settings rows
            // are untouched.
            if (oldVersion < 4) {
                if (!db.objectStoreNames.contains(STORE_BOOKMARKS)) {
                    const bookmarks = db.createObjectStore(STORE_BOOKMARKS, { keyPath: "id" });
                    bookmarks.createIndex(INDEX_BOOKMARKS_CIRCUIT_ID, "circuitId", { unique: false });
                }
            }
            // Future Sprint 33+ migrations land here:
            //   if (oldVersion < 5) { … assets store … }
        };
        req.onsuccess = () => {
            cachedDb = req.result;
            // Surface unexpected version-change events (other tab upgrade)
            // by closing the cached connection so the next openDb call
            // reopens fresh.
            cachedDb.onversionchange = () => {
                cachedDb?.close();
                cachedDb = null;
            };
            resolve(cachedDb);
        };
        req.onerror = () => {
            reject(req.error ?? new Error("openDb: unknown IDB error"));
        };
        req.onblocked = () => {
            reject(new Error("openDb: blocked — another tab holds a stale connection"));
        };
    });
    return openPromise;
}
/**
 * Test helper — closes and forgets the cached connection so the
 * next `openDb()` opens a fresh instance. Production code should
 * never call this; production lifecycle keeps the connection alive
 * for the lifetime of the page.
 */
export function __resetDbForTests() {
    cachedDb?.close();
    cachedDb = null;
    openPromise = null;
}
/**
 * Promise-friendly request wrapper. Many IDB operations expose an
 * `IDBRequest` that resolves via `onsuccess` / `onerror` callbacks;
 * this helper turns them into awaitable promises.
 */
export function requestToPromise(req) {
    return new Promise((resolve, reject) => {
        req.onsuccess = () => {
            resolve(req.result);
        };
        req.onerror = () => {
            reject(req.error ?? new Error("requestToPromise: unknown IDB error"));
        };
    });
}
/**
 * Promise-friendly transaction completion wrapper.
 */
export function transactionDone(tx) {
    return new Promise((resolve, reject) => {
        tx.oncomplete = () => {
            resolve();
        };
        tx.onerror = () => {
            reject(tx.error ?? new Error("transactionDone: unknown IDB error"));
        };
        tx.onabort = () => {
            reject(tx.error ?? new Error("transactionDone: aborted"));
        };
    });
}
//# sourceMappingURL=db.js.map